/**
 * ConfirmDialogContainer — shared confirm/destructive dialog
 * molecule (SE-5524).
 *
 * Single source of truth for every "are you sure?" confirmation in the app
 * (delete/archive/discard/remove flows). Feature containers keep their
 * mutation logic and render this molecule with pre-formatted copy; the
 * molecule owns only presentation state:
 *
 * - the optional type-to-confirm input value (case-insensitive match against
 *   `requireTypedConfirmation.expectedText`, reset when the dialog closes),
 * - the optional free-text REASON (SE-5500) recorded with the action, gating
 *   confirm until it is non-blank and reset on the same key-reset boundary,
 * - testID derivation (`${testID}-cancel|confirm|input|icon`) with explicit
 *   overrides for legacy ids that predate the derivation convention and are
 *   pinned by e2e specs (e.g. `keep-editing-btn`, `confirm-discard-btn`).
 * @module components/molecules/ConfirmDialog/ConfirmDialogContainer
 */

import React, { memo, useCallback, useId, useState } from "react";

import ConfirmDialogView from "./ConfirmDialogView";
import type {
  ConfirmDialogPresentation,
  ConfirmDialogTone,
} from "./ConfirmDialogView";

/** Public props for the shared confirm/destructive dialog molecule. */
export interface ConfirmDialogProps {
  /** Whether the dialog is visible. */
  readonly isOpen: boolean;
  /** Invoked on cancel, backdrop press, or the close X. */
  readonly onClose: () => void;
  /**
   * Invoked when the user confirms (only when the confirm gate is open).
   * Receives the TRIMMED reason when `requireReason` is configured, and
   * `undefined` otherwise — callers that take no argument are unaffected.
   */
  readonly onConfirm: (reason?: string) => void;
  /** Dialog title. */
  readonly title: string;
  /**
   * Pre-formatted body copy — no JSX. Pass a string[] to render stacked
   * lines (e.g. a file name or a report-count warning as its own line).
   *
   * Optional (SE-8567): a confirmation whose whole body is `bodySlot` omits
   * it rather than passing an empty array. Omitted renders no lines — the
   * same output `body={[]}` produced — and with no `bodySlot` either the
   * dialog is its title and footer alone.
   */
  readonly body?: string | readonly string[];
  /**
   * Extra body content, rendered under the lines. See the View prop of the
   * same name for why this is a slot rather than more props.
   */
  readonly bodySlot?: React.ReactNode;
  /** Optional Lucide icon rendered in a tone circle above the title. */
  readonly icon?: React.ElementType;
  /** Semantic tone — drives the icon circle + confirm button pairing. */
  readonly tone?: ConfirmDialogTone;
  /** Which shape the dialog takes; defaults to the house `alert` style. */
  readonly presentation?: ConfirmDialogPresentation;
  /** Cancel button label. */
  readonly cancelLabel?: string;
  /** Confirm button label (caller owns loading-label variants). */
  readonly confirmLabel: string;
  /** Loading state on the confirm button (disables both buttons). */
  readonly isConfirming?: boolean;
  /** Extra caller-owned confirm gate. */
  readonly isConfirmDisabled?: boolean;
  /** Gates confirm until the user types `expectedText` (case-insensitive). */
  readonly requireTypedConfirmation?: {
    readonly expectedText: string;
    readonly placeholder?: string;
  };
  /**
   * SE-5500 — renders a required multi-line REASON field and gates confirm
   * until it holds non-whitespace text. A whitespace-only reason keeps the
   * gate closed on purpose: backends that normalise blank rationales to NULL
   * would otherwise record "no reason given" for an action the product
   * requires a justification for.
   */
  readonly requireReason?: {
    /** Accessible label for the field. */
    readonly accessibilityLabel: string;
    /** Placeholder copy. */
    readonly placeholder: string;
    /**
     * Hard cap on the reason, surfaced by a live `n/max` counter. Caps here
     * are a REJECTION boundary, not a truncation one — the field refuses
     * further input rather than silently shortening a submitted reason.
     */
    readonly maxLength: number;
    /** Helper line under the field. */
    readonly helperText?: string;
  };
  /**
   * Error copy rendered inside the dialog — e.g. a destructive mutation that
   * failed. Shown in place of nothing; the dialog stays open so the reason the
   * user typed survives a retry.
   */
  readonly errorMessage?: string;
  /** Render inside a native RN Modal so the dialog stacks over parent modals. */
  readonly useRNModal?: boolean;
  /** Dialog width preset. */
  readonly size?: "sm" | "md";
  /** Root testID; child ids derive as `${testID}-cancel|confirm|input|icon`. */
  readonly testID?: string;
  /** Legacy override for the cancel button testID. */
  readonly cancelTestID?: string;
  /** Legacy override for the confirm button testID. */
  readonly confirmTestID?: string;
  /** Legacy override for the typed-confirmation input testID. */
  readonly inputTestID?: string;
}

/**
 * Derives a child testID from the root testID unless explicitly overridden.
 * @param override - Explicit legacy testID, wins when provided
 * @param testID - Root testID to derive from
 * @param suffix - Child suffix (`cancel`, `confirm`, `input`, `icon`)
 * @returns The resolved child testID, or undefined when neither is set.
 */
const deriveTestID = (
  override: string | undefined,
  testID: string | undefined,
  suffix: string
): string | undefined =>
  override ?? (testID ? `${testID}-${suffix}` : undefined);

/** No body lines — the shared value an omitted `body` resolves to. */
const NO_BODY_LINES: readonly string[] = [];

/**
 * Normalises the `body` prop to the View's stacked lines. An omitted body is
 * no lines at all, which is exactly what the `body={[]}` callers rendered
 * before `body` became optional (SE-8567); a string is one line.
 * @param body - The caller's body copy, if any
 * @returns The lines to render, first line leading.
 */
const toBodyLines = (
  body: string | readonly string[] | undefined
): readonly string[] => {
  if (body === undefined) return NO_BODY_LINES;
  return typeof body === "string" ? [body] : body;
};

/**
 * Inner stateful component owning the typed-confirmation state, the required
 * reason text, and testID derivation. Keyed by `isOpen` in the outer container
 * so both fields reset whenever the dialog CLOSES (key-reset pattern — a
 * reopened dialog never starts pre-confirmed). The key deliberately does not
 * change while the dialog stays open, which is what lets a failed destructive
 * action keep the reason the user already typed (SE-5500).
 * @param props - Component props (see {@link ConfirmDialogProps})
 * @param props.isOpen - Whether the dialog is visible
 * @param props.onClose - Cancel / backdrop / close-X handler
 * @param props.onConfirm - Confirm handler (gated by the container)
 * @param props.title - Dialog title
 * @param props.body - Pre-formatted body copy (string or stacked lines); omitted renders no lines
 * @param props.bodySlot - Extra body content rendered under the lines
 * @param props.icon - Optional Lucide icon for the tone circle
 * @param props.tone - Semantic tone pairing
 * @param props.presentation - Which shape the dialog takes
 * @param props.cancelLabel - Cancel button label
 * @param props.confirmLabel - Confirm button label
 * @param props.isConfirming - Whether the confirm action is in flight
 * @param props.isConfirmDisabled - Extra caller-owned confirm gate
 * @param props.requireTypedConfirmation - Type-to-confirm gate config
 * @param props.requireReason - Required free-text reason config (SE-5500)
 * @param props.errorMessage - Error copy rendered inside the dialog
 * @param props.useRNModal - Render inside a native RN Modal for stacking
 * @param props.size - Dialog width preset
 * @param props.testID - Root testID (children derive from it)
 * @param props.cancelTestID - Legacy cancel button testID override
 * @param props.confirmTestID - Legacy confirm button testID override
 * @param props.inputTestID - Legacy typed-confirmation input testID override
 * @returns The confirm/destructive dialog view.
 */
const ConfirmDialogStateful = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  body,
  bodySlot,
  icon,
  tone = "danger",
  presentation,
  cancelLabel = "Cancel",
  confirmLabel,
  isConfirming = false,
  isConfirmDisabled = false,
  requireTypedConfirmation,
  requireReason,
  errorMessage,
  useRNModal,
  size = "md",
  testID,
  cancelTestID,
  confirmTestID,
  inputTestID,
}: ConfirmDialogProps) => {
  const titleId = useId();
  const [typedText, setTypedText] = useState("");
  const [reasonText, setReasonText] = useState("");

  const isTypedGateClosed =
    requireTypedConfirmation !== undefined &&
    typedText.toLowerCase() !==
      requireTypedConfirmation.expectedText.toLowerCase();

  // Trim-then-measure, so a field holding only spaces/newlines never satisfies
  // a required reason (SE-5500).
  const trimmedReason = reasonText.trim();
  const isReasonGateClosed =
    requireReason !== undefined && trimmedReason.length === 0;

  const resolvedConfirmDisabled =
    isConfirmDisabled || isTypedGateClosed || isReasonGateClosed;

  const handleConfirm = useCallback(() => {
    if (resolvedConfirmDisabled || isConfirming) return;
    onConfirm(requireReason ? trimmedReason : undefined);
  }, [
    resolvedConfirmDisabled,
    isConfirming,
    onConfirm,
    requireReason,
    trimmedReason,
  ]);

  // Backdrop and close-X share this handler with the footer cancel; every
  // dismissal path must stay inert while the destructive mutation is in
  // flight, not only the disabled footer buttons.
  const handleClose = useCallback(() => {
    if (isConfirming) return;
    onClose();
  }, [isConfirming, onClose]);

  // SE-6888: `typedText` drives the gate comparison ONLY — it is deliberately
  // not fed back into the field. The field is uncontrolled, mounted empty and
  // thereafter owned by the platform, which is why fast typing, a paste, or an
  // autocomplete insertion cannot be clobbered by a stale React value landing
  // late. Emptiness on reopen comes from the key-reset remount below, so this
  // is a constant rather than `typedText` (which would read as controlled and
  // invite the defect straight back in).
  const typedConfirmation = requireTypedConfirmation
    ? {
        defaultValue: "",
        placeholder:
          requireTypedConfirmation.placeholder ??
          `Type "${requireTypedConfirmation.expectedText}" to confirm`,
        onChangeText: setTypedText,
      }
    : undefined;

  const reasonField = requireReason
    ? {
        value: reasonText,
        placeholder: requireReason.placeholder,
        accessibilityLabel: requireReason.accessibilityLabel,
        maxLength: requireReason.maxLength,
        helperText: requireReason.helperText,
        onChangeText: setReasonText,
      }
    : undefined;

  // Which shape the dialog takes is the container's decision; the View
  // renders the resolved flag (SE-8019).
  const isDocument = presentation === "document";

  return (
    <ConfirmDialogView
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={title}
      titleId={titleId}
      bodyLines={toBodyLines(body)}
      bodySlot={bodySlot}
      icon={icon}
      tone={tone}
      isDocument={isDocument}
      cancelLabel={cancelLabel}
      confirmLabel={confirmLabel}
      isConfirming={isConfirming}
      isConfirmDisabled={resolvedConfirmDisabled}
      typedConfirmation={typedConfirmation}
      reasonField={reasonField}
      errorMessage={errorMessage}
      useRNModal={useRNModal}
      size={size}
      testID={testID}
      iconTestID={testID ? `${testID}-icon` : undefined}
      cancelTestID={deriveTestID(cancelTestID, testID, "cancel")}
      confirmTestID={deriveTestID(confirmTestID, testID, "confirm")}
      inputTestID={deriveTestID(inputTestID, testID, "input")}
      reasonTestID={testID ? `${testID}-reason` : undefined}
      errorTestID={testID ? `${testID}-error` : undefined}
    />
  );
};

/**
 * Container for the shared confirm/destructive dialog. Keys the stateful
 * inner component by open state so the typed-confirmation input resets
 * whenever the dialog closes (key-reset pattern, no effects needed).
 * @param props - Component props (see {@link ConfirmDialogProps})
 * @returns The keyed stateful dialog.
 */
const ConfirmDialogContainer = (props: ConfirmDialogProps) => (
  <ConfirmDialogStateful key={props.isOpen ? "open" : "closed"} {...props} />
);

ConfirmDialogContainer.displayName = "ConfirmDialogContainer";

export default memo(ConfirmDialogContainer);
